import { useState, useCallback, useMemo } from 'react';
import { sendContactEmail } from '../utils/emailService';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialFormState = {
  name: '',
  email: '',
  subject: '',
  message: '',
};

const validateFields = (data) => {
  const errors = {};

  if (!data.name.trim()) {
    errors.name = 'Name is required';
  } else if (data.name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters';
  }

  if (!data.email.trim()) {
    errors.email = 'Email is required';
  } else if (!EMAIL_REGEX.test(data.email.trim())) {
    errors.email = 'Please enter a valid email address';
  }

  if (!data.subject.trim()) {
    errors.subject = 'Subject is required';
  } else if (data.subject.trim().length < 5) {
    errors.subject = 'Subject must be at least 5 characters';
  }

  if (!data.message.trim()) {
    errors.message = 'Message is required';
  } else if (data.message.trim().length < 10) {
    errors.message = 'Message must be at least 10 characters';
  }

  return errors;
};

export const useContactForm = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const allErrors = useMemo(() => validateFields(formData), [formData]);

  const visibleErrors = useMemo(() => {
    const result = {};
    Object.keys(touched).forEach((field) => {
      if (touched[field] && allErrors[field]) {
        result[field] = allErrors[field];
      }
    });
    return result;
  }, [allErrors, touched]);

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setTouched(prev => ({ ...prev, [field]: true }));
    setSubmitError('');
  }, []);

  const isValid = useMemo(() => Object.keys(allErrors).length === 0, [allErrors]);

  const submitForm = useCallback(async () => {
    setTouched({ name: true, email: true, subject: true, message: true });

    const errors = validateFields(formData);
    if (Object.keys(errors).length > 0) return false;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      await sendContactEmail({
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
      });
      setIsSuccess(true);
      setFormData(initialFormState);
      setTouched({});
      return true;
    } catch {
      setSubmitError('Something went wrong. Please try again or email us directly.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData]);

  const resetForm = useCallback(() => {
    setFormData(initialFormState);
    setTouched({});
    setIsSuccess(false);
    setSubmitError('');
  }, []);

  return {
    formData,
    errors: visibleErrors,
    isSubmitting,
    isSuccess,
    submitError,
    updateField,
    submitForm,
    resetForm,
    isValid,
  };
};
